import { type RefObject, useCallback, useMemo, useRef } from 'react';

import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from 'use-query-params';

import {
  useGridApiRef,
  type GridFilterModel,
} from '@packages/fenrir/components/datagrid';
import { usePagination } from '@packages/hooks/use-pagination/use-pagination';

import type { AssetsSnackbarRef } from '../../../../components/assets-snackbar/assets-snackbar';
import {
  MAX_FILTER_VALUES_LIMIT,
  MAX_FILTERS_LIMIT,
} from '../../../../components/assets-table/asset-table.constants';
import { updateGridStateToSession } from '../../../../components/assets-table/update-data-grid-state';
import { paginationConfig } from '../../../assets.constants';
import { useAssetTypeParam } from '../../use-asset-type-param';
import type { Filters } from '../filters';

import type { FilterQueryParamType } from './filter-map';
import { queryParamsMap } from './filter-map';
import {
  getInitialFilterModel,
  getFormattedQueryParams,
  getQueryParamsFromFilterModel,
  getAPIFiltersFromQueryParams,
} from './filter-transform-utils';

export const useAssetFilters = (snackbarRef?: RefObject<AssetsSnackbarRef>) => {
  const { t } = useTranslation('ads');
  const gridAPIRef = useGridApiRef();

  const [queryParams, setQueryParams] = useQueryParams(queryParamsMap);
  const [, setPagination] = usePagination({
    pageSize: paginationConfig.assetsList.pageSize,
  });
  const assetType = useAssetTypeParam();
  const filterModelRef = useRef<GridFilterModel>(
    getInitialFilterModel(queryParams, assetType),
  );
  const setFilterModel = useCallback((newFilterModel: GridFilterModel) => {
    filterModelRef.current = newFilterModel;
  }, []);

  const initialLoad = useRef(true);
  const filters = useMemo<Filters>(() => {
    //TODO: S2CX-16747: Restructure this code to avoid dependencies between filters and queryParams
    if (initialLoad.current && window.location.search === '') {
      initialLoad.current = false;
      return getAPIFiltersFromQueryParams(
        getQueryParamsFromFilterModel(
          getInitialFilterModel(queryParams, assetType),
        ),
      );
    }
    return getAPIFiltersFromQueryParams(queryParams);
  }, [queryParams, assetType]);

  const setPaginationAndQueryParams = useCallback(
    (formattedQueryParams: FilterQueryParamType) => {
      setQueryParams(formattedQueryParams);

      if (!isEqual(queryParams, formattedQueryParams)) {
        setPagination(0);
      }
    },
    [queryParams, setPagination, setQueryParams],
  );

  // This is used by custom AssetsFilters component to set the filters
  const handleSetFilters = useCallback(
    (newFilters: Partial<Filters>) => {
      const { lastScan, lastSeen } = newFilters;
      const [lastScanFrom, lastScanTo] = lastScan ?? [];
      const [lastSeenFrom, lastSeenTo] = lastSeen ?? [];

      // Validate empty values and converts the queryParams to the format required in queryParams
      const formattedQueryParams = getFormattedQueryParams({
        ...newFilters,
        lastScanFrom,
        lastScanTo,
        lastSeenFrom,
        lastSeenTo,
      });

      // Set the queryParams. And pagination if queryParams have changed
      setPaginationAndQueryParams(formattedQueryParams);
    },
    [setPaginationAndQueryParams],
  );

  /**
   * Takes the current filter model state and updates the query params, which triggers updating filters
   * @returns void
   * @internal This is used by data grid component to apply the filters
   */
  const applyFilters = useCallback(() => {
    // Convert the current filterModel to queryParams
    const queryParamsFromFilterModel = getQueryParamsFromFilterModel(
      filterModelRef.current,
    );

    // Set the queryParams. And pagination if queryParams have changed
    setPaginationAndQueryParams(queryParamsFromFilterModel);

    // Set the session storage to update grid state
    const gridState = gridAPIRef.current?.exportState();
    updateGridStateToSession(gridState, assetType);
  }, [gridAPIRef, assetType, setPaginationAndQueryParams]);

  /**
   * Takes the new filter model and checks if it exceeds the limits.
   * If it does, it shows an error snackbar and does not update the filter model
   * else it updates the filter model.
   * @param newFilterModel - The new filter model to validate
   * @returns void
   * @internal Works as a wrapper for setFilterModel to handle limits
   */
  const setFilterModelWithLimits = useCallback(
    (newFilterModel: GridFilterModel) => {
      const activeFiltersCount = Object.values(newFilterModel.items).length;

      const hasExceededFilterValuesLimit = newFilterModel.items.some(
        (item) =>
          Array.isArray(item.value) &&
          item.value.length > MAX_FILTER_VALUES_LIMIT,
      );

      if (activeFiltersCount > MAX_FILTERS_LIMIT) {
        snackbarRef?.current?.showCustomAlert({
          message: t('assetsTable.filterLimitMessage'),
        });
        gridAPIRef.current?.setFilterModel(filterModelRef.current);
        return;
      } else if (hasExceededFilterValuesLimit) {
        snackbarRef?.current?.showCustomAlert({
          message: t('assetsTable.filterValuesLimitMessage'),
        });
        gridAPIRef.current?.setFilterModel(filterModelRef.current);
        return;
      }
      setFilterModel(newFilterModel);
    },
    [gridAPIRef, setFilterModel, snackbarRef, t],
  );

  return {
    gridAPIRef,
    filters,
    setFilters: handleSetFilters,
    filterModel: filterModelRef.current,
    setFilterModel: setFilterModelWithLimits,
    applyFilters,
  };
};
